import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { CloseEvent } from '../app.main.component';

/**
 * Interface that components must implement to use the save and quit functionality.
 * Components should check if they have unsaved changes and provide a save method.
 */
export interface ComponentWithSaveAndQuit {
    /**
     * Check if the component is in a state where it should allow navigation without warning.
     * Typically this returns true when in consultation/view-only mode.
     * @returns true if navigation should be allowed without confirmation
     */
    modeConsultation(): boolean;

    /**
     * Save the current changes. Should return an Observable that emits true on success, false on failure.
     * @returns Observable<boolean> indicating save success or failure
     */
    enregistrerModifications: Observable<boolean>;
}

/**
 * Service to handle save and quit functionality across components.
 * Provides a reusable way to handle save/quit/cancel actions.
 * 
 * This service works with the existing p-confirmDialog in app.main.component.html
 * which uses a custom template with a split button for "Save and Quit" and "Quit without saving".
 * 
 * Usage in a component:
 * ```typescript
 * async canDeactivate(): Promise<boolean> {
 *     if (this.saveAndQuitService.shouldSkipConfirmation(this)) {
 *         return Promise.resolve(true);
 *     }
 *     
 *     // Show dialog and wait for user action
 *     // Handle the CloseEvent in your component's onClose method
 *     return await this.handleCloseEvent(...);
 * }
 * 
 * async handleCloseEvent(event: CloseEvent, onHideDialog: () => void): Promise<boolean> {
 *     return await this.saveAndQuitService.handleCloseEvent(event, this, onHideDialog);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SaveAndQuitService {

    constructor() {}
    /**
     * Checks if the confirmation dialog should be skipped.
     * @param component The component that implements ComponentWithSaveAndQuit interface
     * @returns true if navigation should proceed without confirmation
     */
    shouldSkipConfirmation(component: ComponentWithSaveAndQuit): boolean {
        return component.modeConsultation();
    }

    /**
     * Handles the close event from the user's selection.
     * This method processes the SaveAndQuit, Quit, or Cancel actions.
     * 
     * @param event The CloseEvent selected by the user
     * @param component The component that implements ComponentWithSaveAndQuit interface
     * @param onHideDialog Optional callback to hide the dialog (should be provided to hide the confirmation dialog)
     * @returns Promise<boolean> - true if navigation should proceed, false if it should be cancelled
     */
    async handleCloseEvent(event: CloseEvent, component: ComponentWithSaveAndQuit, onHideDialog?: () => void): Promise<boolean> {
        switch (event) {
            case CloseEvent.SaveAndQuit:
                // Save and then quit
                try {
                    const saveSuccess = await lastValueFrom(component.enregistrerModifications);
                    if (saveSuccess && onHideDialog) {
                        onHideDialog();
                    }
                    return saveSuccess; // Proceed if save was successful
                } catch (error) {
                    console.error('Error during save:', error);
                    return false; // Don't proceed if save failed
                }

            case CloseEvent.Quit:
                // Quit without saving
                if (onHideDialog) {
                    onHideDialog();
                }
                return true;

            case CloseEvent.Cancel:
                // Cancel - stay on the page
                if (onHideDialog) {
                    onHideDialog();
                }
                return false;

            default:
                return false;
        }
    }
}
